/* eslint-disable no-script-url */
import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import vehicleFallbackImage from '../../assets/images/Vehicle/vehicle-fallback-image.png';
import { MARKET } from '../../constants';
import { FilteredVehicle, GalleryImagesType } from '../../types';
import Modal from '../Modal';
import Location from './Location';
import {
  ImageContainer,
  Button,
  VehicleInfo,
  VehicleName,
  VehicleTrim,
  CpoBLOCK,
  CpoIcon,
  Milage,
  Legal,
  TextContainer,
  SingleVehicleImage,
  StatusContainer,
} from './VehicleCard.styles';
import CPOLogo from '../../assets/images/CertifiedBadges/CPO';
import Prices from './Prices';
import ImagesGallery from '../ImagesGallery/ImagesGallery';
import Status from './Status';
import CtaGroup from './CtaGroup/CtaGroup';

interface VehicleCardProps {
  vehicle: FilteredVehicle;
  marketType: string;
  vehicleDetailsUrl: string;
  isImageGalleryOn: boolean;
  isLoadingUrl: boolean;
  isGoingToTier3: boolean;
}

const T1VehicleCard: React.FC<VehicleCardProps> = ({
  vehicle,
  marketType,
  isImageGalleryOn,
  vehicleDetailsUrl,
  isLoadingUrl,
  isGoingToTier3,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';
  const isEnglish = i18n.language === 'en';
  const {
    modelName,
    modelPrice,
    modelPriceRetail,
    dealerName,
    trimName,
    modelImageFromScs,
    vehicleType,
    modelMileage,
    warrantyType,
    isAvailableNow,
    bodyType,
    saleOrderType,
    vehicleOrderStatus,
    distanceFromUser,
    dealerRegion,
    galleryImages,
    typedPrices,
    kvpsSyncId,
    id,
    modelYear,
  } = vehicle;

  const isMarketCA = marketType === 'CAN';
  const isNewVehicle = vehicleType === 'new';
  const isCPO = warrantyType === 'used-car-warranty-type.plus';
  const isReservedVehicle = saleOrderType?.includes('SOLD');

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const digitalLeadsData = JSON.stringify({
    vehicleId: id,
    vehicleType,
    market: marketType,
    presetForm: 'contact-dealer',
    dealerId: kvpsSyncId,
  });

  const onModalOpen = useCallback((e: any, title?: string, text?: string | JSX.Element) => {
    e.preventDefault();
    e.stopPropagation();
    setModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const correctPrice = useMemo(() => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const retail = typedPrices.find((price) => price.type === 'retail');
    const retailAmount = retail?.amount;
    // changed to only use .amount until .formatted becomes more consistent
    return formatter.format(retailAmount!);
    // const isMarketUSA = marketType === 'USA';
    // if (
    //   vehicleType === 'used' &&
    //   !isMarketUSA &&
    //   modelPriceRetail.includes('{F[value]}') &&
    //   retailAmount
    // ) {
    //   return formatter.format(retailAmount).substring(1);
    // }
    // if (vehicleType === 'new' && !isMarketUSA && retailAmount) return modelPriceRetail.substring(1);
    // return modelPriceRetail;
  }, [typedPrices]);

  const formattedPrice = useMemo(() => {
    const isMarketUSA = marketType === 'USA';
    // let priceFormatted = `${correctPrice.replace(' ', '')}`;
    let priceFormatted = correctPrice;

    if (!isMarketUSA && !isEnglish) {
      priceFormatted = `${correctPrice.replace('$', '').replace(',', ' ').replace('.', ',')} $ CA`;
    }

    return priceFormatted;
  }, [correctPrice, isEnglish, marketType]);

  // FIXME: Delete this function when backend will be fixed
  const deleteHPfromTrim = (trim: string) => {
    if (!trim) return null;
    if (trim.includes('!!')) return null;
    if (!trim.includes('HP') && !trim.includes('kW')) return trim;

    const trimNameArray = trim.split(' ');

    const indexOfHP = trimNameArray.includes('HP')
      ? trimNameArray.indexOf('HP')
      : trimNameArray.indexOf('kW');

    const isNumberElementBeforeHP = /^\d+$/.test(trimNameArray[indexOfHP - 1]?.replace(',', ''));

    if (isNumberElementBeforeHP) {
      trimNameArray.splice(indexOfHP - 1, 2);
      return trimNameArray.join(' ');
    }
    trimNameArray.splice(indexOfHP, 1);
    return trimNameArray.join(' ');
  };

  const popoverDescription = useMemo(() => {
    if (isReservedVehicle) return t('vlp.vehicleResults.vehicleCard.reserved');
    if (isAvailableNow && isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableNowCAN.popoverDescription');
    if (isAvailableNow && !isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableNowUSA.popoverDescription');
    if (!isAvailableNow && isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableSoon.popoverDescriptionCAN');
    return t('vlp.vehicleResults.vehicleCard.availableSoon.popoverDescriptionUSA');
  }, [isAvailableNow, isMarketCA, isReservedVehicle, t]);

  const altFunction = useCallback(() => {
    if (modelImageFromScs && modelImageFromScs.includes('default-images')) {
      return t('vlp.vehicleResults.vehicleCard.vehicleFallbackAlt', { modelName });
    }
    return t('vlp.vehicleResults.vehicleCard.vehicleStandardAlt', { modelName });
  }, [modelImageFromScs, modelName, t]);

  const statusForDot = useMemo(() => {
    if (isReservedVehicle) return 'RESERVED';
    if (isAvailableNow) return 'AVAILABLE';
    return 'PIPELINE';
  }, [isAvailableNow, isReservedVehicle]);

  const vehicleStatus = useMemo(() => {
    if (isReservedVehicle) return 'Reserved';
    if (isAvailableNow)
      return t(`vlp.vehicleResults.vehicleCard.availableNow${MARKET}.availabilityLabel`);
    if (!isAvailableNow && vehicleType === 'new' && vehicleOrderStatus === 'Intransit From Factory')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.factory');
    if (!isAvailableNow && vehicleType === 'new' && vehicleOrderStatus === 'Port Stock')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.port');
    if (!isAvailableNow && vehicleType === 'new' && vehicleOrderStatus === 'Intransit Inland')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.inland');
    if (!isAvailableNow && vehicleType === 'new' && vehicleOrderStatus == null)
      return t('vlp.vehicleResults.vehicleCard.orderStatus.contactDealer');

    return '';
  }, [isReservedVehicle, isAvailableNow, t, vehicleType, vehicleOrderStatus]);

  const priceLegalText = useMemo(() => {
    if (marketType === 'CAN') return t('vlp.vehicleResults.vehicleCard.dealerListPriceLegalText');

    if (marketType === 'USA' && vehicleType === 'new' && modelYear === '2025')
      return t('vlp.vehicleResults.vehicleCard.msrpLegalTextUSA-2025');

    if (marketType === 'USA' && vehicleType === 'new')
      return t('vlp.vehicleResults.vehicleCard.msrpLegalTextUSA');

    // marketType === 'USA' && vehicleType === 'used'
    return (
      <Trans
        i18nKey="vlp.vehicleResults.vehicleCard.dealerPriceLegalText"
        // the global reset css imported in several feature apps
        // contains globally applied oppinionated styling
        components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
      />
    );
  }, [marketType, vehicleType, isAvailableNow, t, modelYear]);

  const galleryImagesVar = useMemo(() => {
    // if there are at least one image in galleryImages array return that
    // otherwise return modelImageFromScs or vehicleFallbackImage
    if (galleryImages?.length > 0) return galleryImages;
    const modelImage = modelImageFromScs || vehicleFallbackImage;
    const singleImageObject: GalleryImagesType = [{ type: null, url: modelImage }];
    return singleImageObject;
  }, [galleryImages, modelImageFromScs]);

  const modelImage = modelImageFromScs || vehicleFallbackImage;

  const unitOfMeasurement = marketType === 'CAN' ? 'km' : 'miles';

  const renderCpoBlock = useMemo(() => {
    return (
      <CpoBLOCK isCPO={isCPO}>
        {isCPO && (
          <CpoIcon>
            <CPOLogo />
          </CpoIcon>
        )}
        {vehicleType === 'used' && modelMileage && (
          <Milage data-testid="model-mileage" data-cy="vehicle-card-mileage">
            <Trans
              i18nKey="vlp.vehicleResults.vehicleCard.mileage"
              values={{ modelMileage, unitOfMeasurement }}
              components={{ 0: <br /> }}
            />
          </Milage>
        )}
      </CpoBLOCK>
    );
  }, [isCPO, modelMileage, unitOfMeasurement, vehicleType]);

  const renderGallery = useMemo(() => {
    return (
      <ImageContainer>
        {isImageGalleryOn ? (
          <ImagesGallery
            count={galleryImagesVar?.length}
            isNewVehicle={isNewVehicle}
            isReservedVehicle={isReservedVehicle}
            images={galleryImagesVar}
            bodyType={bodyType}
            alt={altFunction()}
            data-cy="vehicle-card-image-gallery"
          />
        ) : (
          <SingleVehicleImage
            src={modelImage}
            alt={altFunction()}
            isNewVehicle={isNewVehicle}
            isReservedVehicle={isReservedVehicle}
            data-cy="vehicle-card-simgle-image"
          />
        )}
      </ImageContainer>
    );
  }, [
    altFunction,
    bodyType,
    galleryImagesVar,
    isImageGalleryOn,
    isNewVehicle,
    isReservedVehicle,
    modelImage,
  ]);

  const renderStatus = useMemo(() => {
    return (
      <Status
        popoverDescription={popoverDescription}
        vehicleStatus={vehicleStatus}
        statusForDot={statusForDot}
      />
    );
  }, [popoverDescription, statusForDot, vehicleStatus]);

  const renderCardContent = useMemo(() => {
    return (
      <>
        <VehicleInfo>
          {isNewVehicle && <StatusContainer>{renderStatus}</StatusContainer>}
          <VehicleName data-testid="model-name" data-cy="vehicle-card-model-name">
            {modelName}
          </VehicleName>
          <VehicleTrim data-testid="trim-name" data-cy="vehicle-card-trim-name">
            {deleteHPfromTrim(trimName)}
          </VehicleTrim>
          <Location
            dealerName={dealerName}
            unitOfMeasurement={unitOfMeasurement}
            distanceFromUser={distanceFromUser}
            isNewVehicle={isNewVehicle}
          />
          {!isNewVehicle && renderCpoBlock}
        </VehicleInfo>
        {renderGallery}
        <Prices
          modelPrice={modelPrice}
          modelPriceRetail={formattedPrice}
          vehicleType={vehicleType}
          dealerRegion={dealerRegion}
          isAvailableNow={isAvailableNow}
          marketType={marketType}
          onModalOpen={onModalOpen}
        />
        {!isMarketCA && (
          <CtaGroup
            hasIcon={isGoingToTier3}
            vehicleDetailsUrl={vehicleDetailsUrl}
            isLoading={isLoadingUrl}
            digitalLeadsData={digitalLeadsData}
          />
        )}
      </>
    );
  }, [
    dealerName,
    dealerRegion,
    digitalLeadsData,
    distanceFromUser,
    formattedPrice,
    isAvailableNow,
    isGoingToTier3,
    isLoadingUrl,
    isMarketCA,
    isNewVehicle,
    marketType,
    modelName,
    modelPrice,
    onModalOpen,
    renderCpoBlock,
    renderGallery,
    renderStatus,
    trimName,
    unitOfMeasurement,
    vehicleDetailsUrl,
    vehicleType,
  ]);

  if (
    // If we get null, we hide it
    isAvailableNow === null ||
    // if availableNow is boolean false and
    // vehicleOrderStatus not one of the below three, we hide it
    (!isAvailableNow &&
      vehicleOrderStatus &&
      !['Intransit From Factory', 'Port Stock', 'Intransit Inland'].includes(vehicleOrderStatus))
    // if availableNow is boolean false and
    // saleOrderType not the one below, we hide it
    // (!isAvailableNow &&
    //   saleOrderType &&
    //   !['SHIP - Deliverable Inventory Order'].includes(saleOrderType))
  )
    return null;

  return (
    <>
      {isMarketCA ? (
        <Button
          isReservedVehicle={isReservedVehicle}
          href={vehicleDetailsUrl}
          tabIndex={0}
          data-cy="vehicle-card-button"
        >
          {renderCardContent}
        </Button>
      ) : (
        // if it's not Canadian market we don't have hover effect
        // so we there is no need to have cursor and href and tabIndex for the tile
        <Button isReservedVehicle={isReservedVehicle} data-cy="vehicle-card-button" noCursor>
          {renderCardContent}
        </Button>
      )}

      <Modal isOpen={modalOpen} onClose={onModalClose} data-cy="vehicle-card-legal-modal">
        <Legal>
          <TextContainer>{priceLegalText}</TextContainer>
        </Legal>
      </Modal>
    </>
  );
};

export default T1VehicleCard;
